import type { CrossOrderNettingLineResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { formatCurrency } from "Infrastructure/Utilities/currencyFormat";
import { paymentDetailAtom } from "Order/Atom/paymentAtom";
import { OrderPaths } from "Order/Routes/Routing";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { Link, Stack, Typography } from "@mui/material";
import { useAtomValue } from "jotai";
import { Link as RouterLink } from "react-router";

type CrossOrderNettingWidgetProps = {
	currency: string;
	country: string;
};

type NettingLineProps = CrossOrderNettingWidgetProps & {
	line: CrossOrderNettingLineResponseDto;
	label: string;
	partnerLabel: string;
	color: "error.main" | "success.main";
};

const paymentResource = Resources.base.payment.shared;

function NettingLine({
	line,
	label,
	partnerLabel,
	color,
	currency,
	country,
}: NettingLineProps) {
	return (
		<Stack direction="row" justifyContent="space-between" gap={2}>
			<Stack>
				<Typography variant="body2">{label}</Typography>
				<Typography variant="caption" color="text.secondary">
					{partnerLabel} #{line.partnerId}
				</Typography>
			</Stack>
			<Stack alignItems="flex-end">
				<Link component={RouterLink} to={OrderPaths.Payment.path(line.orderId)}>
					#{line.orderId}
				</Link>
				<Typography variant="body2" color={color} fontWeight={600}>
					{formatCurrency(country, currency, line.amount)}
				</Typography>
			</Stack>
		</Stack>
	);
}

export function CrossOrderNettingWidget({
	currency,
	country,
}: CrossOrderNettingWidgetProps) {
	const { t } = useResource();
	const paymentDetail = useAtomValue(paymentDetailAtom);
	const recoveredFrom = paymentDetail?.recoveredFrom ?? [];
	const deductions = paymentDetail?.deductions ?? [];

	if (recoveredFrom.length === 0 && deductions.length === 0) {
		return null;
	}

	return (
		<BaseCard>
			<Stack gap={2}>
				<Typography variant="h5">
					{t(paymentResource.crossOrderCharges)}
				</Typography>
				{recoveredFrom.map((line) => (
					<NettingLine
						key={`recovered-${line.orderId}-${line.partnerId}`}
						line={line}
						label={t(paymentResource.recoveredFrom)}
						partnerLabel={t(paymentResource.partner)}
						color="success.main"
						currency={currency}
						country={country}
					/>
				))}
				{deductions.map((line) => (
					<NettingLine
						key={`deduction-${line.orderId}-${line.partnerId}`}
						line={line}
						label={t(paymentResource.deductionForCharge)}
						partnerLabel={t(paymentResource.partner)}
						color="error.main"
						currency={currency}
						country={country}
					/>
				))}
			</Stack>
		</BaseCard>
	);
}
